'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useVirtualizer } from '../../hooks/use-virtualizer.tsx'
import { Text } from '../text/text.tsx'
import { SelectOption } from './select-option.tsx'
import { useSelectContext } from './use-select.ts'
import { getPlaceholderItem, optionHeight } from './utils.ts'

type SelectOptionsProps = {
  close: () => void
}

export const SelectOptions = <ValueType extends string = string>({
  close,
}: SelectOptionsProps) => {
  const {
    filteredOptions,
    deselectable,
    initialOptionCount,
    openComplete,
    options,
    onLoadRemaining,
    placeholder,
  } = useSelectContext<ValueType>()

  const scrollRef = useRef<HTMLDivElement>(null)
  const showPlaceholder = deselectable

  const placeholderItem = useMemo(() => {
    if (deselectable) {
      return getPlaceholderItem(placeholder)
    }
  }, [deselectable, placeholder])

  const extras = showPlaceholder ? 1 : 0
  const totalCount = filteredOptions.length + extras
  const currentCount = options.length + extras
  const currentCountWithLoader =
    options.length + extras + (filteredOptions.length > options.length ? 1 : 0) // add a loader row if there are more options

  const [virtualizer] = useVirtualizer({
    count: currentCountWithLoader,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => optionHeight,
    overscan: 10,
  })

  // we're delaying loading the rest to make opening the popout faster
  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to use the function VALUE here
  useEffect(() => {
    const items = virtualizer.getVirtualItems()
    const [lastItem] = [...items].reverse()

    if (!lastItem) {
      return
    }

    if (
      openComplete &&
      lastItem.index >= currentCount &&
      filteredOptions.length > initialOptionCount &&
      totalCount > currentCount
    ) {
      onLoadRemaining()
    }
  }, [
    openComplete,
    currentCount,
    filteredOptions.length,
    initialOptionCount,
    totalCount,
    onLoadRemaining,
    virtualizer.getVirtualItems(),
  ])

  if (options.length === 0) {
    return (
      <div className="overflow-y-auto hide-scroll px-8 py-4">
        <Text textColor="tertiary">No options match your search!</Text>
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      className="w-full overflow-x-hidden overflow-y-auto scroll-thin"
    >
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          if (placeholderItem && virtualRow.index === 0) {
            return (
              <SelectOption
                key={virtualRow.key}
                close={close}
                item={placeholderItem}
                virtualItem={virtualRow}
                placeholder
              />
            )
          }

          if (virtualRow.index > currentCount && openComplete) {
            return (
              <SelectOption
                key={virtualRow.key}
                close={close}
                item={{ label: 'Loading...', value: 'loading' }}
                virtualItem={virtualRow}
                loadingRow
              />
            )
          }

          const option = options[virtualRow.index - (showPlaceholder ? 1 : 0)]
          if (!option) {
            return null
          }
          return (
            <SelectOption
              key={virtualRow.key}
              close={close}
              item={option}
              virtualItem={virtualRow}
            />
          )
        })}
      </div>
    </div>
  )
}
